import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  sortWidgetsByZ,
  updateWidgetById,
  deleteWidgetFromTree,
  insertWidgetCloneAfter,
  moveWidgetEarlierInTree,
  moveWidgetLaterInTree,
  findWidgetById,
  getSlideLayout,
  ONBOARDING_HEADING_FONT_LEVELS,
  headingSemanticTagFromLevel,
  ONBOARDING_BODY_FONT_LEVELS,
  normalizedFontSizeLevel,
  onboardingTextColorFromWidget,
  CANVAS_LOGICAL_W,
  CANVAS_LOGICAL_H,
  readWidgetSizeForPreview,
  mapWidgetSizePatchForPreview,
  widgetInnerPaddingStyle,
  widgetOuterMarginStyle,
  imageWidgetFrameStyle,
  slideCanvasBackgroundStyle,
  normalizedImageBoxShape,
  imageSquareNormalizedHFromW,
  ONBOARDING_EMOJI_SIZE_LEVELS,
  emojiToNotoUrl,
} from "@/lib/onboardingCanvas";
import { InlineEditableBullets, InlineEditableRichText } from "@/components/onboarding/OnboardingInlineEdit";
import { embedSrcForWidget, tiktokNeedsCreatorOembed } from "@/lib/onboardingEmbed";
import { parseChipsItems, ChipIcon } from "@/lib/onboardingChips";
import { parseColumnsRow, cloneWidgetTree } from "@/lib/onboardingColumnsRow";
import { cn } from "@/lib/utils";
import TikTokCreatorEmbed from "@/components/onboarding/TikTokCreatorEmbed";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import OnboardingSlideEnterFirew0rksAscii from "@/components/onboarding/OnboardingSlideEnterFirew0rksAscii";

const CanvasStageContext = React.createContext(null);

function useCanvasStageContext() {
  const v = React.useContext(CanvasStageContext);
  if (!v) throw new Error("OnboardingCanvasStage: contexto no disponible");
  return v;
}

function WidgetShell({ textAlign, verticalAlign, className, children, clip = false }) {
  const v =
    verticalAlign === "center" ? "justify-center" : verticalAlign === "end" ? "justify-end" : "justify-start";
  const items =
    textAlign === "center" ? "items-center" : textAlign === "right" ? "items-end" : "items-start";
  const ta =
    textAlign === "center"
      ? "text-center"
      : textAlign === "right"
        ? "text-right"
        : textAlign === "justify"
          ? "text-justify"
          : "text-left";
  return (
    <div className={cn("flex flex-col w-full min-h-0 min-w-0 ob-canvas-pad", v, items)}>
      <div
        className={cn(
          "w-full min-w-0",
          clip ? "min-h-0 max-h-full overflow-hidden" : "min-h-0 overflow-visible",
          ta,
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

function WidgetBody({ w, animated, animIndex }) {
  const { readOnly, onWidgetsChange, slideLayout, previewDevice } = useCanvasStageContext();
  const delay = animated ? Math.min(animIndex, 24) * 0.07 : 0;
  const effectiveProps = readWidgetSizeForPreview(w, previewDevice);
  const ta = effectiveProps.textAlign;
  const va = effectiveProps.verticalAlign;
  const effectiveFontSizeLevel = effectiveProps.fontSizeLevel;
  const commitText = (text) => onWidgetsChange?.((list) => updateWidgetById(list, w.id, { text }));

  /**
   * Ref que almacena la última src para la que ya se aplicó el autoajuste de proporciones.
   * Se inicializa perezosamente en el PRIMER render al valor actual de imageSrc para que las
   * imágenes ya guardadas no se redimensionen al recargar la página.
   */
  const autoSizedSrcRef = useRef(undefined);
  if (autoSizedSrcRef.current === undefined) {
    autoSizedSrcRef.current = (w.imageSrc || "").trim();
  }

  const wrap = (node) =>
    animated ? (
      <div
        className="ob-widget-reveal flex min-h-0 w-full min-w-0 flex-1 flex-col"
        style={{ animationDelay: `${delay}s` }}
      >
        {node}
      </div>
    ) : (
      node
    );

  if (w.type === "columns_row") {
    const d = parseColumnsRow(w.text);
    return wrap(
      <ColumnsRowBody rowWidget={w} columnCount={d.columnCount} cells={d.cells} animated={animated} animIndex={animIndex} />
    );
  }

  if (w.type === "embed_tiktok") {
    const src = embedSrcForWidget(w.type, w.text);
    const needCreator = tiktokNeedsCreatorOembed(w.text);
    return wrap(
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col rounded-md bg-neutral-100">
        {src ? (
          <iframe
            title="TikTok"
            src={src}
            className="min-h-0 w-full flex-1 border-0"
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation"
            allow="encrypted-media; fullscreen; autoplay"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : needCreator ? (
          <TikTokCreatorEmbed url={(w.text || "").trim()} className="min-h-[14rem] max-h-[min(70vh,28rem)] py-2" />
        ) : (
          <p className="ob-canvas-small text-neutral-400 px-2 text-center leading-snug">
            Pega la URL del vídeo o del perfil (@usuario) en el panel lateral.
          </p>
        )}
      </div>
    );
  }

  if (w.type === "embed_instagram") {
    const src = embedSrcForWidget(w.type, w.text);
    return wrap(
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col rounded-md bg-neutral-100">
        {src ? (
          <iframe
            title="Instagram"
            src={src}
            className="min-h-0 w-full flex-1 border-0"
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation"
            allow="encrypted-media; fullscreen; autoplay"
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <p className="ob-canvas-small text-neutral-400 px-2 text-center leading-snug">
            Pega la URL de una publicación o reel (instagram.com/p/… o /reel/…).
          </p>
        )}
      </div>
    );
  }

  if (w.type === "divider") {
    const lineStyle = ["solid", "dashed", "dotted"].includes(w.lineStyle) ? w.lineStyle : "solid";
    const lineColor = w.accent && /^#[0-9a-fA-F]{6}$/.test(w.accent) ? w.accent : "#d4d4d4";
    const dashStyle =
      lineStyle === "dashed"
        ? { borderTopStyle: "dashed", borderTopWidth: "2px", borderTopColor: lineColor }
        : lineStyle === "dotted"
        ? { borderTopStyle: "dotted", borderTopWidth: "2px", borderTopColor: lineColor }
        : { borderTopStyle: "solid", borderTopWidth: "1px", borderTopColor: lineColor };
    return wrap(
      <div className="flex items-center justify-center w-full min-h-[2px] pointer-events-none py-1">
        <div className="w-full" style={dashStyle} />
      </div>
    );
  }
  if (w.type === "shape") {
    const bg = w.accent && w.accent.startsWith("#") ? w.accent : "#eef2ff";
    return wrap(<div className="w-full min-h-[2rem] rounded-2xl pointer-events-none opacity-90" style={{ background: bg }} />);
  }

  if (w.type === "image") {
    const src = (w.imageSrc || "").trim();
    const size = readWidgetSizeForPreview(w, previewDevice);
    const shape = normalizedImageBoxShape(w.imageBoxShape);
    const lock = w.lockAspectRatio !== false;
    const imgFitClass = lock ? "object-cover" : "object-fill";
    const br = Math.max(0, Math.min(50, Number(w.borderRadius) || 0));
    const cap = (w.text || "").trim();
    const uniformRadius = shape === "circle" ? "9999px" : br <= 0 ? "0px" : `min(${(br / 50) * 26}cqi, 80px)`;

    /**
     * Primera carga de src: ajusta h respecto a w (rect: proporción natural si lock; cuadrado/círculo: marco cuadrado en lienzo 16:9).
     */
    const handleImgAutoSize = !readOnly && onWidgetsChange
      ? (e) => {
          if (!src || src === autoSizedSrcRef.current) return;
          autoSizedSrcRef.current = src;
          const img = e.currentTarget;
          const { naturalWidth: nw, naturalHeight: nh } = img;
          if (!nw || !nh) return;
          const wFrac = Math.max(0.1, Math.min(1, Number(size.w) || 1));
          if (shape === "rect") {
            if (!lock) return;
            const newH = wFrac * (CANVAS_LOGICAL_W / CANVAS_LOGICAL_H) * (nh / nw);
            const clampedH = Math.max(0.05, Math.min(0.26, newH));
            onWidgetsChange((list) =>
              updateWidgetById(list, w.id, mapWidgetSizePatchForPreview({ h: clampedH }, previewDevice))
            );
          } else {
            const nhN = imageSquareNormalizedHFromW(wFrac);
            onWidgetsChange((list) =>
              updateWidgetById(list, w.id, mapWidgetSizePatchForPreview({ h: nhN }, previewDevice))
            );
          }
        }
      : undefined;

    const frameExtra = imageWidgetFrameStyle(w);
    return wrap(
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-1">
        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col bg-neutral-100/80 overflow-hidden",
            shape === "circle" && "rounded-full"
          )}
          style={{
            ...(shape === "circle" ? {} : { borderRadius: uniformRadius }),
            ...frameExtra,
          }}
        >
          {src ? (
            <img
              src={src}
              alt={cap || "Imagen"}
              className={cn("h-full min-h-0 w-full flex-1", imgFitClass, shape === "circle" && "rounded-full")}
              draggable={false}
              onLoad={handleImgAutoSize}
            />
          ) : (
            <div className="flex h-full min-h-[4rem] w-full items-center justify-center ob-canvas-small text-neutral-400">Sin imagen</div>
          )}
        </div>
        {cap ? (
          <p
            className="ob-canvas-small shrink-0 line-clamp-2"
            style={{
              color: onboardingTextColorFromWidget(w, "caption"),
              fontSize: ONBOARDING_BODY_FONT_LEVELS[normalizedFontSizeLevel(effectiveFontSizeLevel) - 1],
            }}
          >
            {cap}
          </p>
        ) : null}
      </div>
    );
  }

  if (w.type === "chips") {
    const items = parseChipsItems(w.text);
    const isSafeHref = (h) => /^https?:\/\//i.test(h) || /^data:/i.test(h);
    const chLv = normalizedFontSizeLevel(effectiveFontSizeLevel);
    const chFs = ONBOARDING_BODY_FONT_LEVELS[chLv - 1];
    const chCol = onboardingTextColorFromWidget(w, "chip");
    const justifyClass = ta === "center" ? "justify-center" : ta === "right" ? "justify-end" : "justify-start";
    return wrap(
      <div
        className={cn("flex min-h-0 w-full min-w-0 flex-wrap content-start items-center gap-1.5 sm:gap-2", justifyClass)}
        style={{ color: chCol, fontSize: chFs }}
      >
        {items.length === 0 ? (
          <span className="ob-canvas-small opacity-60">Añade enlaces o archivos</span>
        ) : (
          items.map((it) => {
            const label = (it.label || "").trim() || (it.kind === "file" ? "Archivo" : "Enlace");
            const href = (it.url || "").trim();
            const isHttp = /^https?:\/\//i.test(href);
            const common =
              "inline-flex max-w-full items-center gap-1 rounded-full border border-current/25 bg-white/95 px-2.5 py-1 font-medium text-inherit shadow-sm transition hover:bg-white";
            if (!href || !isSafeHref(href)) {
              return (
                <span key={it.id} className={cn(common, "cursor-default opacity-60")}>
                  {it.icon && <ChipIcon iconId={it.icon} size="0.9em" />}
                  {label}
                </span>
              );
            }
            return (
              <a
                key={it.id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                download={it.kind === "file" && !isHttp ? label : undefined}
                className={cn(common, "hover:underline")}
              >
                {it.icon && <ChipIcon iconId={it.icon} size="0.9em" />}
                <span className="truncate">{label}</span>
              </a>
            );
          })
        )}
      </div>
    );
  }
  if (w.type === "sticky") {
    const stLv = normalizedFontSizeLevel(effectiveFontSizeLevel);
    const stCol = onboardingTextColorFromWidget(w, "sticky");
    const stFs = ONBOARDING_BODY_FONT_LEVELS[stLv - 1];
    return wrap(
      <WidgetShell textAlign={ta} verticalAlign={va}>
        <InlineEditableRichText
          tag="p"
          className="ob-canvas-small leading-snug whitespace-pre-wrap break-words"
          style={{ color: stCol, fontSize: stFs }}
          value={w.text || ""}
          readOnly={readOnly}
          onCommit={commitText}
        />
      </WidgetShell>
    );
  }
  if (w.type === "bullets") {
    const blv = normalizedFontSizeLevel(effectiveFontSizeLevel);
    const bcol = onboardingTextColorFromWidget(w, "body");
    const bfs = ONBOARDING_BODY_FONT_LEVELS[blv - 1];
    return wrap(
      <WidgetShell textAlign={ta} verticalAlign={va}>
        <InlineEditableBullets
          className="[text-align:inherit]"
          style={{ color: bcol, fontSize: bfs }}
          value={w.text || ""}
          readOnly={readOnly}
          onCommit={commitText}
        />
      </WidgetShell>
    );
  }
  if (w.type === "heading") {
    const hLv = normalizedFontSizeLevel(effectiveFontSizeLevel);
    const hCol = onboardingTextColorFromWidget(w, "heading");
    const hFs = ONBOARDING_HEADING_FONT_LEVELS[hLv - 1];
    return wrap(
      <WidgetShell textAlign={ta} verticalAlign={va}>
        <InlineEditableRichText
          tag="h2"
          className="ob-canvas-heading m-0 font-semibold tracking-tight leading-tight break-words"
          style={{ color: hCol, fontSize: hFs }}
          value={w.text || ""}
          readOnly={readOnly}
          onCommit={commitText}
        />
      </WidgetShell>
    );
  }
  if (w.type === "emoji") {
    const emojiChar = w.text || "🎉";
    const emojiLv = normalizedFontSizeLevel(effectiveFontSizeLevel);
    const emojiSize = ONBOARDING_EMOJI_SIZE_LEVELS[emojiLv - 1];
    const notoUrl = emojiToNotoUrl(emojiChar);
    return wrap(
      <WidgetShell textAlign={ta} verticalAlign={va}>
        <div className="flex items-center justify-center w-full min-h-0" style={{ fontSize: emojiSize }}>
          {notoUrl ? (
            <img
              src={notoUrl}
              alt={emojiChar}
              className="block"
              style={{ width: "1em", height: "1em", objectFit: "contain" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextSibling?.style && (e.currentTarget.nextSibling.style.display = "inline");
              }}
              loading="lazy"
              decoding="async"
            />
          ) : null}
          <span
            aria-hidden={!!notoUrl}
            style={{ display: notoUrl ? "none" : "inline", fontSize: emojiSize, lineHeight: 1 }}
          >
            {emojiChar}
          </span>
        </div>
      </WidgetShell>
    );
  }

  const bLv = normalizedFontSizeLevel(effectiveFontSizeLevel);
  const bCol = onboardingTextColorFromWidget(w, "body");
  const bFs = ONBOARDING_BODY_FONT_LEVELS[bLv - 1];
  return wrap(
    <WidgetShell textAlign={ta} verticalAlign={va}>
      <InlineEditableRichText
        tag="p"
        className="ob-canvas-body whitespace-pre-wrap break-words"
        style={{ color: bCol, fontSize: bFs }}
        value={w.text || ""}
        readOnly={readOnly}
        onCommit={commitText}
      />
    </WidgetShell>
  );
}

function ColumnsRowBody({ rowWidget, columnCount, cells, animated, animIndex }) {
  const { readOnly, slideLayout } = useCanvasStageContext();
  const rowId = rowWidget.id;
  const g = slideLayout.widgetGapRem;
  return (
    <div
      className={cn(
        "w-full min-w-0 grid",
        columnCount === 3 ? "grid-cols-1 sm:grid-cols-3"
          : columnCount === 1 ? "grid-cols-1"
          : "grid-cols-1 sm:grid-cols-2"
      )}
      style={{ gap: `${g}rem` }}
    >
      {cells.map((col, colIdx) => (
        <div
          key={`${rowId}-c${colIdx}`}
          className="min-w-0 flex flex-col rounded-lg border border-neutral-200/80 bg-neutral-50/40 p-1.5 sm:p-2"
          style={{ gap: `${Math.max(0.25, g * 0.65)}rem` }}
        >
          {col.length === 0 && readOnly ? (
            <span className="ob-canvas-small text-neutral-400 text-center py-2">Vacío</span>
          ) : null}
          {col.map((cw, i) => (
            <NestedWidgetSlot key={cw.id} w={cw} rowId={rowId} colIndex={colIdx} animated={animated} animIndex={animIndex + i + 1} />
          ))}
        </div>
      ))}
    </div>
  );
}

function NestedWidgetSlot({ w, rowId, colIndex, animated, animIndex }) {
  return (
    <CanvasWidgetSlot w={w} animated={animated} animIndex={animIndex} columnsRowContext={{ rowId, colIndex }} classNameInner="!w-full" />
  );
}

function CanvasWidgetSlot({ w, animated, animIndex, columnsRowContext = null, classNameInner = "" }) {
  const dragRef = useRef(null);
  const widgetsRef = useRef(null);
  const { onWidgetsChange, onSelectWidget, selectedIds, rootWidgets, readOnly, previewDevice } = useCanvasStageContext();
  const size = readWidgetSizeForPreview(w, previewDevice);
  const sizeW = Number(size.w);
  const sizeH = Number(size.h);

  useEffect(() => {
    widgetsRef.current = rootWidgets;
  }, [rootWidgets]);

  const toNorm = useCallback((clientX, clientY, el) => {
    if (!el) return { nx: 0, ny: 0 };
    const r = el.getBoundingClientRect();
    return { nx: (clientX - r.left) / r.width, ny: (clientY - r.top) / r.height };
  }, []);

  const applyResize = useCallback(
    (e, canvasEl) => {
      const d = dragRef.current;
      if (!d || !onWidgetsChange || !canvasEl) return;
      const { nx, ny } = toNorm(e.clientX, e.clientY, canvasEl);
      const dx = nx - d.startNx;
      const dy = ny - d.startNy;
      const corner = d.resizeCorner || "se";
      let nw = d.origW;
      let nh = d.origH;

      if (d.widgetType === "divider") {
        nw = Math.max(d.minW, d.origW + dx);
        nh = Math.max(0.015, Math.min(0.2, d.origH + dy * 0.5));
      } else if (d.widgetType === "image") {
        const shape = d.imageBoxShape || "rect";
        if (shape === "square" || shape === "circle") {
          const signs = {
            se: [1, 1],
            nw: [-1, -1],
            ne: [1, -1],
            sw: [-1, 1],
            e: [1, 0],
            w: [-1, 0],
            s: [0, 1],
            n: [0, -1],
          };
          const [sx, sy] = signs[corner] || [1, 1];
          const denom = sx !== 0 && sy !== 0 ? 2 : 1;
          const delta = (dx * sx + dy * sy) / denom;
          nw = Math.max(d.minW, Math.min(1, d.origW + delta));
          nh = Math.max(d.minH, Math.min(1, nw * (CANVAS_LOGICAL_W / CANVAS_LOGICAL_H)));
        } else if (e.shiftKey) {
          const s0 = Math.min(d.origW, d.origH);
          const delta = dx + dy;
          const ms = Math.max(Math.max(d.minW, d.minH), Math.min(1, s0 + delta));
          nw = nh = ms;
        } else {
          switch (corner) {
            case "se":
              nw = d.origW + dx;
              nh = d.origH + dy;
              break;
            case "ne":
              nw = d.origW + dx;
              nh = d.origH - dy;
              break;
            case "sw":
              nw = d.origW - dx;
              nh = d.origH + dy;
              break;
            case "nw":
              nw = d.origW - dx;
              nh = d.origH - dy;
              break;
            case "e":
              nw = d.origW + dx;
              break;
            case "w":
              nw = d.origW - dx;
              break;
            case "s":
              nh = d.origH + dy;
              break;
            case "n":
              nh = d.origH - dy;
              break;
            default:
              nw = d.origW + dx;
              nh = d.origH + dy;
          }
          nw = Math.max(d.minW, Math.min(1, nw));
          nh = Math.max(d.minH, Math.min(1, nh));
        }
      } else if (e.shiftKey) {
        const s0 = Math.min(d.origW, d.origH);
        const delta = dx + dy;
        const ms = Math.max(Math.max(d.minW, d.minH), Math.min(1, s0 + delta));
        nw = nh = ms;
      } else {
        switch (corner) {
          case "se":
            nw = d.origW + dx;
            nh = d.origH + dy;
            break;
          case "ne":
            nw = d.origW + dx;
            nh = d.origH - dy;
            break;
          case "sw":
            nw = d.origW - dx;
            nh = d.origH + dy;
            break;
          case "nw":
            nw = d.origW - dx;
            nh = d.origH - dy;
            break;
          case "e":
            nw = d.origW + dx;
            break;
          case "w":
            nw = d.origW - dx;
            break;
          case "s":
            nh = d.origH + dy;
            break;
          case "n":
            nh = d.origH - dy;
            break;
          default:
            nw = d.origW + dx;
            nh = d.origH + dy;
        }
        nw = Math.max(d.minW, Math.min(1, nw));
        nh = Math.max(d.minH, Math.min(1, nh));
      }
      onWidgetsChange((list) =>
        updateWidgetById(list, d.id, mapWidgetSizePatchForPreview({ w: nw, h: nh }, previewDevice))
      );
    },
    [onWidgetsChange, toNorm, previewDevice]
  );

  const endResize = useCallback(() => {
    dragRef.current = null;
  }, []);

  const onResizePointerDown = useCallback(
    (e) => {
      if (readOnly || e.button !== 0 || !onWidgetsChange) return;
      e.preventDefault();
      e.stopPropagation();
      const canvasEl = e.currentTarget.closest("[data-onboarding-canvas]");
      const { nx, ny } = toNorm(e.clientX, e.clientY, canvasEl);
      const minW = w.type === "divider" ? 0.08 : 0.12;
      const minH =
        w.type === "divider" ? 0.012 : w.type === "shape" ? 0.04 : ["embed_tiktok", "embed_instagram"].includes(w.type) ? 0.12 : 0.04;
      const resizeCorner = String(e.currentTarget?.getAttribute?.("data-ob-resize-corner") || "se");
      dragRef.current = {
        id: w.id,
        startNx: nx,
        startNy: ny,
        origW: Math.max(0.04, Math.min(1, sizeW || 0.4)),
        origH: Math.max(0.03, Math.min(1, sizeH || 0.12)),
        minW,
        minH,
        widgetType: w.type,
        imageBoxShape: w.type === "image" ? normalizedImageBoxShape(w.imageBoxShape) : undefined,
        resizeCorner,
      };
      const move = (ev) => applyResize(ev, canvasEl);
      const up = () => {
        endResize();
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {
        /* noop */
      }
    },
    [readOnly, onWidgetsChange, toNorm, w.id, w.type, w.imageBoxShape, sizeW, sizeH, applyResize, endResize]
  );

  const isSel = !readOnly && selectedIds.includes(w.id);
  const zi = animIndex;
  const noPad =
    w.type === "embed_tiktok" || w.type === "embed_instagram" || w.type === "image" || w.type === "columns_row";
  const innerPtr = readOnly ? (noPad || w.type === "chips" ? "pointer-events-auto" : "pointer-events-none") : "pointer-events-auto";

  const imgShape = w.type === "image" ? normalizedImageBoxShape(w.imageBoxShape) : "rect";
  const imageFixedAspectShell = w.type === "image" && (imgShape === "square" || imgShape === "circle");
  const heightFrac = Math.min(1, Math.max(0.02, sizeH || 0.08));
  const pctH = (heightFrac * 100).toFixed(2);
  const embedFullWidth =
    w.type === "embed_tiktok" || w.type === "embed_instagram";
  const resizableMedia = w.type === "image" && imgShape === "rect";
  let shellStyle;
  if (imageFixedAspectShell) {
    const wFrac = Math.max(0.1, Math.min(1, sizeW || 0.3));
    shellStyle = {
      width: `${Math.round(wFrac * 100)}%`,
      maxWidth: "100%",
      aspectRatio: "1",
      minHeight: 0,
      ...(isSel ? { overflow: "visible" } : {}),
    };
  } else if (resizableMedia) {
    const wFrac = Math.max(0.1, Math.min(1, sizeW || 1));
    shellStyle = {
      width: `${Math.round(wFrac * 100)}%`,
      height: `clamp(5.5rem, ${pctH}cqb, min(72vh, 680px))`,
      overflow: isSel ? "visible" : "hidden",
    };
  } else if (embedFullWidth) {
    shellStyle = {
      width: "100%",
      height: `clamp(5.5rem, ${pctH}cqb, min(72vh, 680px))`,
      overflow: isSel ? "visible" : "hidden",
    };
  } else if (w.type === "emoji") {
    shellStyle = {
      width: "100%",
      overflow: isSel ? "visible" : undefined,
    };
  } else {
    shellStyle = {
      width: "100%",
      minHeight: `${pctH}cqmin`,
      overflow: isSel ? "visible" : undefined,
    };
  }
  const showResizeHandle = isSel && onWidgetsChange && w.type !== "divider" && selectedIds.length === 1;
  const innerPadStyle = widgetInnerPaddingStyle(w);
  const outerMarginStyle = widgetOuterMarginStyle(w);
  const shellMergedStyle = outerMarginStyle ? { ...shellStyle, ...outerMarginStyle } : shellStyle;
  const resizeHandleTitle = imageFixedAspectShell
    ? "Arrastra cualquier esquina o borde (marco cuadrado o circular fijo)"
    : "Arrastra esquinas o bordes. Shift: marco 1:1 (solo imagen rectangular)";

  const handleDuplicate = () => {
    const list = widgetsRef.current || [];
    const node = findWidgetById(list, w.id);
    if (!node || !onWidgetsChange) return;
    const clone = cloneWidgetTree(node);
    if (!columnsRowContext && list.some((x) => x.id === w.id)) {
      clone.z = list.reduce((m, x) => Math.max(m, x.z || 0), 0) + 1;
    }
    onWidgetsChange((root) => insertWidgetCloneAfter(root, w.id, clone));
    onSelectWidget?.(clone.id, { shiftKey: false });
  };

  const handleMoveUp = () => {
    if (!onWidgetsChange) return;
    onWidgetsChange((root) => moveWidgetEarlierInTree(root, w.id));
  };

  const handleMoveDown = () => {
    if (!onWidgetsChange) return;
    onWidgetsChange((root) => moveWidgetLaterInTree(root, w.id));
  };

  const handleDelete = () => {
    if (!onWidgetsChange) return;
    onWidgetsChange((root) => deleteWidgetFromTree(root, w.id));
    onSelectWidget?.(null, { prune: [w.id] });
  };

  const resizeHandleLayer =
    showResizeHandle ? (
      <div className="pointer-events-none absolute inset-0 z-[800]" aria-hidden>
        {[
          { corner: "nw", pos: "left-1 top-1", cur: "cursor-nw-resize", aria: "Redimensionar esquina superior izquierda" },
          { corner: "n", pos: "left-1/2 top-1 -translate-x-1/2", cur: "cursor-n-resize", aria: "Redimensionar borde superior" },
          { corner: "ne", pos: "right-1 top-1", cur: "cursor-ne-resize", aria: "Redimensionar esquina superior derecha" },
          { corner: "e", pos: "right-1 top-1/2 -translate-y-1/2", cur: "cursor-e-resize", aria: "Redimensionar borde derecho" },
          { corner: "se", pos: "right-1 bottom-1", cur: "cursor-se-resize", aria: "Redimensionar esquina inferior derecha" },
          { corner: "s", pos: "left-1/2 bottom-1 -translate-x-1/2", cur: "cursor-s-resize", aria: "Redimensionar borde inferior" },
          { corner: "sw", pos: "left-1 bottom-1", cur: "cursor-sw-resize", aria: "Redimensionar esquina inferior izquierda" },
          { corner: "w", pos: "left-1 top-1/2 -translate-y-1/2", cur: "cursor-w-resize", aria: "Redimensionar borde izquierdo" },
        ].map(({ corner, pos, cur, aria }) => (
          <button
            key={corner}
            type="button"
            aria-label={aria}
            title={resizeHandleTitle}
            data-ob-resize-handle="1"
            data-ob-resize-corner={corner}
            className={cn(
              "pointer-events-auto absolute h-3.5 w-3.5 rounded-sm border border-white bg-violet-500 shadow-md ring-2 ring-violet-200/80 sm:h-4 sm:w-4",
              pos,
              cur
            )}
            onPointerDown={onResizePointerDown}
          />
        ))}
      </div>
    ) : null;

  const shell = (
    <div
      className={cn(
        "ob-widget-slot-shell relative box-border touch-none min-h-0 min-w-0 w-full max-w-full flex flex-col",
        isSel ? "ring-2 ring-violet-500 ring-offset-0 rounded-sm" : "",
        readOnly ? "" : "cursor-default",
        w.type === "image" && imgShape === "circle" && (isSel ? "rounded-full" : "rounded-full overflow-hidden")
      )}
      style={shellMergedStyle}
      data-widget-id={w.id}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (!readOnly) onSelectWidget?.(w.id, { shiftKey: e.shiftKey });
      }}
    >
      <div className="relative flex min-h-0 min-w-0 w-full flex-1 flex-col">
        {readOnly || !onWidgetsChange ? (
          <div
            className={cn("w-full min-h-0 min-w-0 flex-1 flex flex-col", innerPtr, !noPad && !innerPadStyle && "ob-canvas-pad", classNameInner)}
            style={innerPadStyle || undefined}
          >
            <WidgetBody w={w} animated={animated} animIndex={zi} />
          </div>
        ) : (
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div
                className={cn("w-full min-h-0 min-w-0 flex-1 flex flex-col", innerPtr, !noPad && !innerPadStyle && "ob-canvas-pad", classNameInner)}
                style={innerPadStyle || undefined}
              >
                <WidgetBody w={w} animated={animated} animIndex={zi} />
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent className="w-44">
              <ContextMenuItem onSelect={handleDuplicate}>Duplicar</ContextMenuItem>
              <ContextMenuItem onSelect={handleMoveUp}>Subir una posición</ContextMenuItem>
              <ContextMenuItem onSelect={handleMoveDown}>Bajar una posición</ContextMenuItem>
              <ContextMenuItem className="text-red-600 focus:text-red-600" onSelect={handleDelete}>
                Eliminar
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        )}
      </div>
      {resizeHandleLayer}
    </div>
  );

  return <div className="flex w-full min-w-0 justify-center items-start">{shell}</div>;
}

export default function OnboardingCanvasStage({
  widgets,
  layoutSlide = null,
  background = "#ffffff",
  readOnly = false,
  animated = false,
  fullscreen = false,
  selectedIds = null,
  onSelectWidget,
  onWidgetsChange,
  className = "",
  /** Capa ASCII detrás de los widgets (vista pública). */
  asciiFireworks = null,
  /** Editor / diálogo: `responsive` = una columna, padding móvil, ancho tipo teléfono (el padre fija el aspecto 9:16). */
  previewDevice,
}) {
  const canvasRef = useRef(null);
  const sorted = useMemo(() => sortWidgetsByZ(widgets || []), [widgets]);
  const normalizedSelectedIds = Array.isArray(selectedIds) ? selectedIds : selectedIds ? [selectedIds] : [];
  const slideLayout = useMemo(() => {
    const base = getSlideLayout(layoutSlide || {});
    if (previewDevice === "responsive") {
      return { ...base, layoutColumns: 1 };
    }
    return base;
  }, [layoutSlide, previewDevice]);

  const canvasBackgroundStyle = useMemo(
    () => slideCanvasBackgroundStyle(layoutSlide || null, background),
    [layoutSlide, layoutSlide?.background, layoutSlide?.backgroundImageSrc, layoutSlide?.backgroundImageFit, background]
  );

  const ctxValue = useMemo(
    () => ({
      onWidgetsChange,
      onSelectWidget,
      selectedIds: normalizedSelectedIds,
      background,
      rootWidgets: widgets || [],
      readOnly,
      slideLayout,
      previewDevice,
    }),
    [onWidgetsChange, onSelectWidget, normalizedSelectedIds, background, widgets, readOnly, slideLayout, previewDevice]
  );

  const gridCols =
    slideLayout.layoutColumns === 3
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      : slideLayout.layoutColumns === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : "grid-cols-1";

  const innerMaxW =
    slideLayout.layoutColumns === 1 ? `min(100%, ${slideLayout.widgetMaxWidthPx}px)` : "100%";

  return (
    <CanvasStageContext.Provider value={ctxValue}>
      <div
        ref={canvasRef}
        data-onboarding-canvas
        className={cn(
          "ob-canvas-root relative flex min-h-0 flex-col overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06),0_12px_28px_rgba(0,0,0,0.08)] rounded-xl",
          fullscreen && "!rounded-none !shadow-none h-full w-full min-h-0 min-w-0",
          previewDevice === "responsive" && "ob-canvas--force-mobile",
          className
        )}
        style={{
          ...canvasBackgroundStyle,
          "--ob-pad-d": `${slideLayout.canvasPadDesktopPx}px`,
          "--ob-pad-m": `${slideLayout.canvasPadMobilePx}px`,
        }}
        onPointerDownCapture={(e) => {
          if (readOnly || !onSelectWidget) return;
          const t = e.target;
          if (typeof t.closest !== "function") return;
          if (t.closest("[data-ob-resize-handle]")) return;
          if (t.closest("[data-widget-id]")) return;
          if (t.closest("[data-radix-popper-content-wrapper]") || t.closest("[role='menu']")) return;
          onSelectWidget(null);
        }}
      >
        {/* Capa de fondo del lienzo (fuera del grid de widgets): no usar flex item directo para el ASCII */}
        {asciiFireworks?.playId > 0 ? (
          <div
            data-ob-ascii-fireworks-backdrop
            className={cn(
              "pointer-events-none absolute inset-0 z-[1] overflow-hidden",
              fullscreen ? "rounded-none" : "rounded-xl"
            )}
          >
            <OnboardingSlideEnterFirew0rksAscii
              key={asciiFireworks.playId}
              playId={asciiFireworks.playId}
              color={asciiFireworks.color}
              onEnded={asciiFireworks.onEnded}
              fullscreen={fullscreen}
            />
          </div>
        ) : null}
        <div
          className={cn(
            "relative z-[2] flex min-h-0 w-full flex-1 items-center justify-center",
            fullscreen && "min-h-0"
          )}
        >
          <div
            className={cn(
              "ob-canvas-stage-inner relative max-h-full min-h-0 w-full grid content-center justify-items-stretch auto-rows-min",
              gridCols
            )}
            style={{
              gap: `${slideLayout.widgetGapRem}rem`,
              maxWidth: innerMaxW,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {sorted.map((w, idx) => (
              <div
                key={w.id}
                className={cn("min-w-0 flex justify-center items-start self-start w-full", w.type === "columns_row" && "col-span-full")}
              >
                <CanvasWidgetSlot w={w} animated={animated} animIndex={idx} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </CanvasStageContext.Provider>
  );
}
